import styled from "styled-components";
import dayjs from "dayjs";
import { AxiosResponse } from "axios";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { DateTimePicker, PropertySelect, DateFilterBadges } from "components/molecules";

import { MAIN_THEME } from "styles/colors";

import {
	ComplexFilterItem,
	ComplexFilterType,
	DateComplexFilter,
	PropertyIdsComplexFilter,
} from "interfaces/filters";

import {
	getDashboardFilter,
	getDashboardReport,
	getDashboardYearReport,
	setDashboardFilter,
} from "state/modules/dashboard";

import { Property } from "models/properties";
import PropertiesClient from "services/api/properties";
import PropertyMultiSelect from "../molecules/PropertyMultiSelect";

const DateDivider = styled.div`
	margin: 0 8px;
	font-size: 20px;
	display: flex;
	align-items: center;
	color: ${MAIN_THEME.TEXT_TERTIARY};
	font-weight: 600;
`;

const BadgesWrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	width: 100%;
`;

const FiltersRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	gap: 20px;

	@media (max-width: 900px) {
		gap: 16px;
	}
`;

const DateFilterGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: nowrap;
`;

const DateFilterGroupRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const FilterTitle = styled.span`
	margin: 0;
	min-width: fit-content;
	color: ${MAIN_THEME.TEXT_PRIMARY};
	font-weight: 500;
	font-size: 13px;
	white-space: nowrap;
`;

const DateRangeContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const Container = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	padding: 12px 16px;
	position: relative;
	z-index: 10;
	gap: 12px;
	border: 1px solid rgba(148, 163, 184, 0.25);
	border-radius: 12px;
`;

const DashboardFilter = (): JSX.Element => {
	const [properties, setProperties] = useState<any[]>([]);

	const dispatch = useDispatch();

	useEffect(() => {
		const loadProperties = async () => {
			try {
				const res = (await PropertiesClient.getProperties({
					offset: 0,
					city: "",
					name: "",
				})) as AxiosResponse;

				const propertiesList = res.data.content as Property[];

				const transformedProperties = propertiesList.map((item) => ({
					label: item.name || "-",
					value: item.id,
				}));

				setProperties(transformedProperties);
			} catch (error) {
				console.log({ error });
			}
		};

		loadProperties();
	}, []);

	const filters = useSelector(getDashboardFilter);

	const dateFilter = useMemo(
		() => filters.find((item) => item.type === ComplexFilterType.DATE),
		[filters]
	) as DateComplexFilter;

	const propertyFilter = useMemo(
		() => filters.find((item) => item.type === ComplexFilterType.PROPERTY_ID),
		[filters]
	) as PropertyIdsComplexFilter;

	const selectedProperties = useMemo(
		() =>
			properties.filter((item) =>
				propertyFilter?.value
					? propertyFilter.value.includes(item.value)
					: false
			),
		[properties, propertyFilter]
	) as any;

	const handleChangeFilter = (updatedFilter: ComplexFilterItem) => {
		const updatedFilters = filters.map((filter: ComplexFilterItem) => {
			if (filter.type === updatedFilter.type) {
				return updatedFilter;
			}

			return filter;
		});

		dispatch(setDashboardFilter(updatedFilters));
		dispatch(getDashboardReport());

		if (updatedFilter.type === ComplexFilterType.PROPERTY_ID) {
			dispatch(getDashboardYearReport());
		}
	};

	const handleChangeDateFrom = (date: string) => {
		const updatedFilter = {
			...dateFilter,
			value: {
				...dateFilter.value,
				dateFrom: dayjs(date).toISOString(),
			},
		} as DateComplexFilter;

		handleChangeFilter(updatedFilter);
	};

	const handleChangeDateTo = (date: string) => {
		const updatedFilter = {
			...dateFilter,
			value: {
				...dateFilter.value,
				dateTo: dayjs(date).toISOString(),
			},
		} as DateComplexFilter;

		handleChangeFilter(updatedFilter);
	};

	const handleChangeProperty = (data: { label: string; value: string }[]) => {
		const updatedFilter = {
			type: ComplexFilterType.PROPERTY_ID,
			value: data.map((item) => item.value),
		} as PropertyIdsComplexFilter;

		handleChangeFilter(updatedFilter);
	};

	const handleBadgeDateRangeSelect = (dateFrom: string, dateTo: string) => {
		const updatedFilter = {
			...dateFilter,
			value: {
				...dateFilter.value,
				dateFrom,
				dateTo,
			},
		} as DateComplexFilter;

		handleChangeFilter(updatedFilter);
	};

	return (
			<Container>
				<BadgesWrapper>
					<DateFilterBadges onDateRangeSelect={handleBadgeDateRangeSelect} />
				</BadgesWrapper>
				<FiltersRow>
					<DateFilterGroup>
						<FilterTitle>Date</FilterTitle>
						<DateTimePicker
							onChange={handleChangeDateFrom}
							dateTime={dateFilter?.value?.dateFrom}
							format="dd/MM/y"
							borderedLeft
							hideClearIcon
							low
							leadingCalendarIcon
						/>
						<DateDivider>→</DateDivider>
						<DateTimePicker
							onChange={handleChangeDateTo}
							dateTime={dateFilter?.value?.dateTo}
							format="dd/MM/y"
							borderedRight
							hideClearIcon
							low
							leadingCalendarIcon
						/>
					</DateFilterGroup>
					<PropertyMultiSelect
						properties={properties}
						onSelect={handleChangeProperty}
						selectedProperties={selectedProperties}
					/>
				</FiltersRow>
			</Container>
	);
};

export default DashboardFilter;
